import { useMemo, useState } from "react";
import { IconRadar2, IconShieldSearch } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { analytics } from "../../analytics/analyticsEvents";
import { getContractById, skills as allSkills, tools as allTools } from "../../content";
import { markedWithoutMeasure } from "../../engine/networkMapReadiness";
import { calculateNetworkMapScore } from "../../engine/networkMapScore";
import { connectedNodes } from "../../engine/networkGraph";
import { layoutNodes } from "../../engine/networkLayout";
import { suitableSkillsForType, suitableToolsForType } from "../../engine/loadout";
import { externalAccessPath, scannerExposedNodeIds } from "../../engine/toolHints";
import { useGameStore } from "../../state/store";
import type { RecommendedActionCode } from "../../types";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import { useGameplaySession } from "../common/useGameplaySession";
import { LoadoutPicker } from "../loadout/LoadoutPicker";
import { useLoadoutNames } from "../loadout/useLoadoutNames";
import { revealedMeasureNodeIds, riskyNodeCount } from "../../engine/networkSkillHints";
import { NetworkCanvas } from "./NetworkCanvas";
import { NodeDetailPanel } from "./NodeDetailPanel";
import { verdictFor } from "./nodeView";
import styles from "./NetworkMap.module.css";

export function NetworkMap(): JSX.Element {
  const activeContractId = useGameStore((state) => state.activeContractId);
  const activeToolIds = useGameStore((state) => state.activeToolIds);
  const activeSkillIds = useGameStore((state) => state.activeSkillIds);
  const networkSecurityLevel = useGameStore((state) =>
    state.activeSkillIds.includes("network_security") ? (state.player.skills.network_security ?? 0) : 0,
  );
  const player = useGameStore((state) => state.player);
  const toggleActiveTool = useGameStore((state) => state.toggleActiveTool);
  const toggleActiveSkill = useGameStore((state) => state.toggleActiveSkill);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const completeTask = useGameStore((state) => state.completeTask);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;
  const nodes = contract?.networkNodes ?? [];

  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  const [measures, setMeasures] = useState<Record<string, RecommendedActionCode>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  useGameplaySession("network_map");
  const { toolName, skillName } = useLoadoutNames();
  const riskyCount = riskyNodeCount(nodes);
  const revealedMeasureIds = useMemo(
    () => new Set(submitted ? [] : revealedMeasureNodeIds(nodes, networkSecurityLevel)),
    [nodes, networkSecurityLevel, submitted],
  );

  const positions = useMemo(() => layoutNodes(nodes), [nodes]);

  const scannedIds = new Set<string>(
    !submitted && activeToolIds.includes("network_scanner") ? scannerExposedNodeIds(nodes) : [],
  );
  const matrixActive = !submitted && activeToolIds.includes("access_matrix_viewer");
  const firstRiskyId = nodes.find((node) => node.isRisky)?.id ?? null;

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
  const linkedIds: ReadonlySet<string> = selectedNode
    ? new Set(connectedNodes(selectedNode, nodes).map((n) => n.id))
    : new Set();

  if (!contract || nodes.length === 0) {
    return (
      <EmptyState
        title={<Trans>Нет карты сети</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("workspace")}>
            <Trans>Назад</Trans>
          </Button>
        }
      />
    );
  }

  const toggle = (id: string): void => {
    if (submitted) {
      return;
    }
    setMarked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pickMeasure = (id: string, code: RecommendedActionCode): void => {
    setMeasures((prev) => ({ ...prev, [id]: code }));
  };

  const submit = (): void => {
    const result = calculateNetworkMapScore(nodes, marked, measures);
    setScore(result);
    setSubmitted(true);
    analytics.taskSubmitted({ type: "network_map", score: result });
  };

  const finish = (): void => {
    completeTask(score);
    navigateTo("workspace");
  };

  const pickerTools = suitableToolsForType("network_map", player.ownedTools, allTools);
  const pickerSkills = suitableSkillsForType("network_map", player.skills, allSkills);
  const missingMeasures = markedWithoutMeasure(marked, measures);
  const canSubmit = marked.size > 0 && missingMeasures.length === 0;

  return (
    <div className={styles.screen}>
      <LoadoutPicker
          tools={pickerTools}
          skills={pickerSkills}
          skillLevels={player.skills}
          activeToolIds={activeToolIds}
          activeSkillIds={activeSkillIds}
          readOnly={submitted}
          onToggleTool={toggleActiveTool}
          onToggleSkill={toggleActiveSkill}
        />
      <Panel
        title={<Trans>Карта сети</Trans>}
        actions={
          submitted ? (
            <span className={cx(styles.scoreTag, "mono")}>
              <Trans>Очки: {score}</Trans>
            </span>
          ) : undefined
        }
      >
        <p className={styles.hint}>
          <Trans>Кликни узел и отметь риск; мера в панели снизу повышает балл.</Trans>
        </p>
        {!submitted && networkSecurityLevel >= 1 && (
          <div className={styles.toolHint}>
            <IconShieldSearch size={14} aria-hidden="true" />
            {skillName("network_security")}:{" "}
            <Trans>рискованных узлов на карте: {riskyCount}</Trans>
          </div>
        )}
        {scannedIds.size > 0 && (
          <div className={styles.toolHint}>
            <IconRadar2 size={14} aria-hidden="true" />
            {toolName("network_scanner")}:{" "}
            <Trans>узлы с внешним доступом помечены значком радара, среди них могут быть безопасные</Trans>
          </div>
        )}

        <div className={cx(styles.layout, selectedNode && styles.layoutWithPanel)}>
        <NetworkCanvas
          nodes={nodes}
          positions={positions}
          selectedNodeId={selectedNodeId}
          linkedIds={linkedIds}
          marked={marked}
          scannedIds={scannedIds}
          submitted={submitted}
          verdictOf={(node) => verdictFor(node, marked.has(node.id), measures[node.id])}
          firstRiskyId={firstRiskyId}
          onSelect={(id) => setSelectedNodeId((prev) => (prev === id ? null : id))}
        />

        {selectedNode && (
          <NodeDetailPanel
            node={selectedNode}
            nodes={nodes}
            isMarked={marked.has(selectedNode.id)}
            measure={measures[selectedNode.id]}
            submitted={submitted}
            revealedMeasure={revealedMeasureIds.has(selectedNode.id) ? (selectedNode.recommendedMeasure ?? undefined) : undefined}
            externalAccess={matrixActive ? externalAccessPath(nodes, selectedNode.id) : undefined}
            onToggle={() => toggle(selectedNode.id)}
            onPickMeasure={(code) => pickMeasure(selectedNode.id, code)}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
        </div>

        <div className={styles.footer}>
          {submitted ? (
            <span data-tutorial="task-continue">
              <Button variant="primary" onClick={finish}>
                <Trans>Продолжить</Trans>
              </Button>
            </span>
          ) : (
            <>
              {marked.size === 0 && (
                <span className={styles.footerHint}>
                  <Trans>Пометь хотя бы один узел как рискованный, чтобы проверить.</Trans>
                </span>
              )}
              {marked.size > 0 && missingMeasures.length > 0 && (
                <span className={styles.footerHint}>
                  <Trans>Выбери меру для отмеченных узлов: {missingMeasures.length}</Trans>
                </span>
              )}
              <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                <Trans>Проверить</Trans>
              </Button>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
